import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useSubscriptionTier } from '@/hooks/useSubscriptionTier';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Tabs,
  Tab,
  Fade,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { Language, NavigateNext } from '@mui/icons-material';
import { standingsService, StandingEntry } from '@/services/standingsService';
import { leagueService } from '@/services/leagueService';
import { scheduleService, ScheduleMatch } from '@/services/scheduleService';
import { positionAnalysisService, LeagueAnalysis } from '@/services/positionAnalysisService';
import { TeamLogo } from '@/components/TeamLogo';
import { LeagueLogo } from '@/components/LeagueLogo';
import { ExpandIndicator } from '@/components/ExpandIndicator';
import { ExpandedMatchView } from '@/components/ExpandedMatchView';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AnalysisLoadingSkeleton } from '@/components/AnalysisLoadingSkeleton';
import { RetryableError } from '@/components/RetryableError';
import { PositionIndicator } from '@/components/PositionIndicator';
import { clearCacheForLeague, clearAllCache } from '@/store/teamMatchCacheSlice';
import { useDelayedSkeleton } from '@/hooks/useDelayedSkeleton';
import { computeAllUnverifiedDeltas } from '@/utils/standingsUtils';

interface League {
  id: string;
  name: string;
  division?: string;
  playoffSpots?: number;
  website?: string;
  logoPath?: string;
  logoUrl?: string;
  seasonCompleted?: boolean;
  playerAssignments?: Array<{
    playerId?: string;
    teamName: string;
  }>;
}

export default function LeagueAnalysisPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { positionAnalysisEnabled } = useFeatureFlags();
  const { hasFeature } = useSubscriptionTier();
  const positionAnalysisAvailable = positionAnalysisEnabled && hasFeature('position_analysis');
  const [league, setLeague] = useState<League | null>(null);
  const [standings, setStandings] = useState<StandingEntry[]>([]);
  const [matches, setMatches] = useState<ScheduleMatch[]>([]);
  const [analysis, setAnalysis] = useState<LeagueAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [teamColors] = useState<Map<string, string>>(new Map());

  const { showSkeleton, showContent, skeletonWasShown } = useDelayedSkeleton({ loading });

  const unverifiedDeltas = useMemo(() => computeAllUnverifiedDeltas(matches), [matches]);

  useEffect(() => {
    if (!leagueId) {
      navigate('/leagues');
      return;
    }
    
    // Handle Chromium browser cache issues on back/forward navigation
    const handlePageShow = () => {
      fetchData();
    };
    
    window.addEventListener('pageshow', handlePageShow);
    fetchData();
    
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [leagueId]);

  // Cleanup cache when component unmounts or navigates away
  useEffect(() => {
    return () => {
      // Clear all cache when leaving the page
      dispatch(clearAllCache());
    };
  }, [dispatch]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Clear cache for this league when refreshing data
      if (leagueId) {
        dispatch(clearCacheForLeague(leagueId));
      }

      const [leagueData, standingsResponse, matchesData] = await Promise.all([
        leagueService.getLeague(leagueId!),
        standingsService.getStandings(leagueId!),
        scheduleService.getSchedule({ leagueId: leagueId!, status: 'COMPLETED', playerIds: [] })
      ]);

      setLeague(leagueData);
      const sortedStandings = standingsResponse.standings;
      setStandings(sortedStandings);
      
      // Fetch position analysis for user teams (only if feature is enabled)
      if (positionAnalysisAvailable) {
        try {
          const analysisData = await positionAnalysisService.getLeagueAnalysis(leagueId!);
          setAnalysis(analysisData);
        } catch (analysisError) {
          // Continue without analysis data - it's optional
          setAnalysis(null);
        }
      } else {
        setAnalysis(null);
      }
      
      // Preload all team logos across all divisions and wait for completion
      const logoPromises = sortedStandings.map(team => {
        const logoSrc = team.logoPath ? `/${team.logoPath}` : team.logoUrl;
        if (logoSrc) {
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Resolve even on error to not block
            img.src = logoSrc;
          });
        }
        return Promise.resolve();
      });
      
      // Also preload league logo
      const leagueLogoSrc = leagueData.logoPath ? `/${leagueData.logoPath}` : leagueData.logoUrl;
      if (leagueLogoSrc) {
        logoPromises.push(new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = leagueLogoSrc;
        }));
      }
      
      // Wait for all logos to load before showing content
      await Promise.all(logoPromises);
      
      const playerAssignments = (leagueData as any).playerAssignments || [];
      
      // Set initial division - prioritize division with user's teams
      if (sortedStandings.length > 0 && !selectedDivision) {
        const userTeamIds = playerAssignments
          .map((pa: any) => pa.globalTeamId)
          .filter(Boolean) as string[];
        const userTeamNames = playerAssignments.map((pa: any) => pa.teamName);

        if (userTeamIds.length > 0 || userTeamNames.length > 0) {
          const userTeam = sortedStandings.find(s =>
            (s.teamId && userTeamIds.includes(s.teamId)) ||
            userTeamNames.some((teamName: string) => s.teamName.toLowerCase().replace(/\s+/g, ' ').trim() === teamName.toLowerCase().replace(/\s+/g, ' ').trim())
          );
          const firstDivision = userTeam?.division || sortedStandings[0].division || 'Main';
          setSelectedDivision(firstDivision);
        } else {
          const firstDivision = sortedStandings[0].division || 'Main';
          setSelectedDivision(firstDivision);
        }
      }
      
      setMatches(matchesData);
      
      // Assign colors to teams
      const colors = ['#1976d2', '#d32f2f', '#388e3c', '#ef6c00', '#7b1fa2', '#00838f', '#c2185b', '#5d4037', '#455a64', '#e64a19', '#00796b', '#303f9f'];
      sortedStandings.forEach((team, idx) => {
        if (!teamColors.has(team.teamName)) {
          teamColors.set(team.teamName, colors[idx % colors.length]);
        }
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load league analysis';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamClick = (teamName: string, teamId?: string) => {
    // Navigate to team analysis page if we have a team ID and it's a user team
    if (teamId && analysis && positionAnalysisAvailable) {
      const teamAnalysis = analysis.teams.find(t => t.teamId === teamId);
      if (teamAnalysis && teamAnalysis.isUserTeam) {
        navigate(`/leagues/${leagueId}/teams/${teamId}/analysis`);
        return;
      }
    }
    
    // Otherwise, toggle expansion for match results
    setExpandedTeam(expandedTeam === teamName ? null : teamName);
    setSelectedTeam(selectedTeam === teamName ? null : teamName);
  };

  const handleExpandClick = (event: React.MouseEvent, teamName: string) => {
    // Stop propagation to prevent team navigation
    event.stopPropagation();
    // Toggle expansion - collapse if already expanded, expand if not
    setExpandedTeam(expandedTeam === teamName ? null : teamName);
    // Keep the selected team logic for other features
    setSelectedTeam(selectedTeam === teamName ? null : teamName);
  };

  const handleTeamKeyDown = (event: React.KeyboardEvent, teamName: string, teamId?: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTeamClick(teamName, teamId);
    }
  };

  const getPPGData = useCallback((divisionTeams: StandingEntry[]) => {
    const maxPPG = 3;
    return divisionTeams.map(team => ({
      name: team.teamName,
      ppg: team.played > 0 ? team.points / team.played : 0,
      percentage: team.played > 0 ? (team.points / team.played / maxPPG) * 100 : 0,
      isPlayoff: league?.playoffSpots ? team.position <= league.playoffSpots : false
    })).sort((a, b) => b.ppg - a.ppg);
  }, [league?.playoffSpots]);

  const getRollingPPGData = useCallback((divisionTeams: StandingEntry[]) => {
    const chartData: any[] = [];

    // Find max form length across division teams
    const maxMatches = Math.max(...divisionTeams.map(team => (team.form || '').length), 0);

    if (maxMatches === 0) return [];

    for (let matchNum = 1; matchNum <= maxMatches; matchNum++) {
      const dataPoint: any = { match: matchNum };

      divisionTeams.forEach(team => {
        const form = team.form || '';
        if (form.length >= matchNum) {
          // Calculate PPG up to this match
          let points = 0;
          for (let i = 0; i < matchNum; i++) {
            const result = form[i];
            if (result === 'W') points += 3;
            else if (result === 'D') points += 1;
          }
          dataPoint[team.teamName] = points / matchNum;
        }
      });

      chartData.push(dataPoint);
    }

    return chartData;
  }, []);

  const divisions = useMemo(() => {
    const divMap = new Map<string, StandingEntry[]>();
    standings.forEach(team => {
      const div = team.division || 'Main';
      if (!divMap.has(div)) {
        divMap.set(div, []);
      }
      divMap.get(div)!.push(team);
    });

    // Get player's team division
    const playerAssignments = (league as any)?.playerAssignments || [];
    const userTeamIds = playerAssignments.map((pa: any) => pa.globalTeamId).filter(Boolean) as string[];
    const userTeamNames = playerAssignments.map((pa: any) => pa.teamName);
    const userTeam = (userTeamIds.length > 0 || userTeamNames.length > 0) ? standings.find(s =>
      (s.teamId && userTeamIds.includes(s.teamId)) ||
      userTeamNames.some((teamName: string) => s.teamName.toLowerCase().replace(/\s+/g, ' ').trim() === teamName.toLowerCase().replace(/\s+/g, ' ').trim())
    ) : null;
    const userDivision = userTeam?.division;

    // Sort divisions: user's division first, then alphabetically
    return Array.from(divMap.entries()).sort((a, b) => {
      if (userDivision) {
        if (a[0] === userDivision) return -1;
        if (b[0] === userDivision) return 1;
      }
      return a[0].localeCompare(b[0]);
    });
  }, [standings, league]);

  // Return loading skeleton while loading
  if (showSkeleton) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Fade in={true} timeout={300}>
          <Box>
            <AnalysisLoadingSkeleton variant="league" />
          </Box>
        </Fade>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <RetryableError
          error={error}
          onRetry={fetchData}
          title="Failed to load league analysis"
          showDetails
        />
      </Container>
    );
  }

  if (!showContent) {
    return null;
  }

  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    if (skeletonWasShown) {
      return (
        <Fade in={showContent} timeout={300}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            {children}
          </Container>
        </Fade>
      );
    }
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {children}
      </Container>
    );
  };

  return (
    <ErrorBoundary>
      <ContentWrapper>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/leagues')}
            sx={{ textDecoration: 'none' }}
          >
            Leagues
          </Link>
          <Typography variant="body2" color="text.primary">
            {league?.division ? `${league.name} - ${league.division}` : league?.name}
          </Typography>
        </Breadcrumbs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'center' },
          gap: 2,
          mb: 1
        }}>
          {(league?.logoPath || league?.logoUrl) && (
            <LeagueLogo
              leagueId={league.id}
              leagueName={league.name}
              size={70}
              logoPath={league.logoPath}
              logoUrl={league.logoUrl}
            />
          )}
          <Typography variant="h4" component="h1" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            {league?.division ? `${league.name} - ${league.division}` : league?.name}
          </Typography>
          {league?.seasonCompleted && (
            <Chip
              label="Season Complete"
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {league?.website && (
            <IconButton
              href={league.website}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              size="small"
              aria-label="Visit league website"
            >
              <Language />
            </IconButton>
          )}
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Complete league analysis and team statistics
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={selectedDivision} 
          onChange={(_, newValue) => setSelectedDivision(newValue)}
          variant="scrollable"
          scrollButtons={divisions.length > 3}
          sx={{
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          {divisions.map(([divisionName]) => (
            <Tab key={divisionName} label={divisionName} value={divisionName} />
          ))}
        </Tabs>
      </Box>

      {divisions.map(([divisionName, divisionStandings]) => {
        const isQoP = divisionStandings.some(t => t.qualityOfPlay !== undefined);
        return selectedDivision === divisionName && (
          <React.Fragment key={divisionName}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2.5fr 1fr' }, gap: 2, scrollMarginTop: 0 }}>
            <Card sx={{ scrollMarginTop: 0 }}>
              <TableContainer sx={{ scrollMarginTop: 0, overflowX: 'hidden' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}></TableCell>
                      <TableCell sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>#</TableCell>
                      <TableCell sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>Team</TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>GP</TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap', p: { xs: 0.5, sm: 1, md: 2 } }}>W-D-L</TableCell>
                      <TableCell align="center" sx={{ display: 'none', p: { xs: 0.5, sm: 1, md: 2 } }}>W</TableCell>
                      <TableCell align="center" sx={{ display: 'none', p: { xs: 0.5, sm: 1, md: 2 } }}>D</TableCell>
                      <TableCell align="center" sx={{ display: 'none', p: { xs: 0.5, sm: 1, md: 2 } }}>L</TableCell>
                      {isQoP ? (
                        <>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>Att</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>Def</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>GD</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>GF</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>GA</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>GD</TableCell>
                        </>
                      )}
                      {isQoP ? (
                        <>
                          <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>QoP</TableCell>
                          <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>PPG</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>PTS</TableCell>
                          <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>PPG</TableCell>
                        </>
                      )}
                      {positionAnalysisAvailable && <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 } }}>Best</TableCell>}
                      <TableCell sx={{ width: 24, p: 0 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {divisionStandings.map((team, index) => {
                      const isPlayoffSpot = league?.playoffSpots && index < league.playoffSpots;
                      const isSelected = team.teamName === selectedTeam;
                      const isExpanded = team.teamName === expandedTeam;
                      const ppg = team.played > 0 ? (team.points / team.played).toFixed(2) : '0.00';
                      const delta = unverifiedDeltas[team.teamName];
                      
                      // Check if this is a user's team and get analysis data
                      const playerAssignments = (league as any)?.playerAssignments || [];
                      const userTeamIds = playerAssignments.map((pa: any) => pa.globalTeamId).filter(Boolean) as string[];
                      const userTeamNames = playerAssignments.map((pa: any) => pa.teamName);
                      const isUserTeam = (team.teamId && userTeamIds.includes(team.teamId)) ||
                        userTeamNames.some((teamName: string) => {
                          if (!teamName) return false;
                          return team.teamName.toLowerCase().replace(/\s+/g, ' ').trim() === teamName.toLowerCase().replace(/\s+/g, ' ').trim();
                        });
                      
                      // Get analysis data for this team
                      const teamAnalysis = analysis?.teams.find(t => t.teamId === team.teamId);
                      
                      return (
                        <React.Fragment key={team.teamName}>
                          <TableRow
                            onClick={() => handleTeamClick(team.teamName, team.teamId)}
                            onKeyDown={(e) => handleTeamKeyDown(e, team.teamName, team.teamId)}
                            tabIndex={0}
                            role="button"
                            aria-expanded={isExpanded}
                            aria-label={isUserTeam && teamAnalysis ? `View analysis for ${team.teamName}` : `${isExpanded ? 'Collapse' : 'Expand'} matches for ${team.teamName}`}
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              bgcolor: isExpanded ? 'action.selected' : (isSelected ? 'action.selected' : (isUserTeam ? 'rgba(76, 175, 80, 0.08)' : 'inherit')),
                              '&:hover': { 
                                bgcolor: isUserTeam && teamAnalysis ? 'primary.50' : 'action.hover'
                              },
                              '&:focus': { 
                                outline: 'none'
                              },
                              pb: 0,
                              WebkitTapHighlightColor: 'transparent',
                              // Visual distinction for clickable user team rows
                              borderLeft: isUserTeam && teamAnalysis ? '3px solid' : 'none',
                              borderLeftColor: isUserTeam && teamAnalysis ? 'primary.main' : 'transparent'
                            }}
                          >
                          <TableCell sx={{ p: { xs: 0.5, sm: 1, md: 2 }, position: 'relative', verticalAlign: 'middle', height: 64 }}>
                            {isPlayoffSpot && (
                              <Box sx={{ 
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: 4, 
                                height: '100%', 
                                bgcolor: 'info.main', 
                                borderRadius: '0 2px 2px 0'
                              }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ p: { xs: 0.5, sm: 1, md: 2 }, verticalAlign: 'middle', height: 64 }}>{index + 1}</TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 1.5, md: 2 }, verticalAlign: 'middle', height: 64, minWidth: { xs: 140, sm: 'auto' } }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <TeamLogo 
                                key={`${team.teamId}-${team.logoPath}-${team.logoUrl}`}
                                teamId={team.teamId} 
                                teamName={team.teamName} 
                                size={24}
                                logoPath={team.logoPath}
                                logoUrl={team.logoUrl}
                              />
                              <Box sx={{ 
                                minHeight: '2.5em',
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%'
                              }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    color: 'inherit',
                                    fontWeight: isUserTeam ? 600 : 400,
                                    width: '100%',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {team.teamName}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>
                            {team.played}{delta && delta.played > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({delta.played})</Box>}
                          </TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap', p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>
                            {team.won}{delta && delta.won > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({delta.won})</Box>}-{team.drawn}{delta && delta.drawn > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({delta.drawn})</Box>}-{team.lost}{delta && delta.lost > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({delta.lost})</Box>}
                          </TableCell>
                          <TableCell align="center" sx={{ display: 'none', p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{team.won}</TableCell>
                          <TableCell align="center" sx={{ display: 'none', p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{team.drawn}</TableCell>
                          <TableCell align="center" sx={{ display: 'none', p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{team.lost}</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{isQoP ? team.attScore ?? 0 : team.goalsFor}</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{isQoP ? team.defScore ?? 0 : team.goalsAgainst}</TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>
                            <Typography
                              sx={{
                                color: team.goalDifference > 0 ? 'success.main' : team.goalDifference < 0 ? 'error.main' : 'inherit'
                              }}
                            >
                              {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                            </Typography>
                          </TableCell>
                          {isQoP ? (
                            <>
                              <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{team.qualityOfPlay}</TableCell>
                              <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{ppg}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{team.points}</TableCell>
                              <TableCell align="center" sx={{ p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>{ppg}</TableCell>
                            </>
                          )}
                          {positionAnalysisAvailable && (
                            <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' }, p: { xs: 0.5, sm: 1, md: 2 }, height: 64 }}>
                              {teamAnalysis ? (
                                <PositionIndicator
                                  currentPosition={teamAnalysis.currentPosition}
                                  bestPosition={teamAnalysis.bestPosition}
                                  likelyPosition={teamAnalysis.likelyPosition}
                                  controlsDestiny={teamAnalysis.controlsDestiny}
                                  contextualLabel={teamAnalysis.bestPosition === 1 ? 'Champion' :
                                    teamAnalysis.bestPosition <= 3 ? 'Top 3' : undefined}
                                  size="small"
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell sx={{ width: 24, p: 0, height: 64 }}>
                            <ExpandIndicator
                              isExpanded={isExpanded}
                              onClick={(e) => handleExpandClick(e, team.teamName)}
                              teamName={team.teamName}
                            />
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={15} sx={{ p: 0 }}>
                              <ExpandedMatchView
                                teamName={team.teamName}
                                leagueId={leagueId!}
                                division={team.division}
                                teamStats={{
                                  won: team.won,
                                  drawn: team.drawn,
                                  lost: team.lost,
                                  goalsFor: team.goalsFor,
                                  goalsAgainst: team.goalsAgainst,
                                  goalDifference: team.goalDifference,
                                  attScore: team.attScore,
                                  defScore: team.defScore,
                                  qualityOfPlay: team.qualityOfPlay
                                }}
                                teamId={team.teamId}
                                isUserTeam={isUserTeam}
                                hideUpcoming
                                unverifiedDelta={delta}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {(() => {
                const playerAssignments = (league as any)?.playerAssignments || [];
                const userTeamIds = playerAssignments.map((pa: any) => pa.globalTeamId).filter(Boolean) as string[];
                const userTeamNames = playerAssignments.map((pa: any) => pa.teamName);
                const userTeam = (userTeamIds.length > 0 || userTeamNames.length > 0) ? divisionStandings.find(t =>
                  (t.teamId && userTeamIds.includes(t.teamId)) ||
                  userTeamNames.some((teamName: string) => t.teamName.toLowerCase().replace(/\s+/g, ' ').trim() === teamName.toLowerCase().replace(/\s+/g, ' ').trim())
                ) : null;
                const hasPlayoffs = league?.playoffSpots;
                
                if (!hasPlayoffs && !userTeam) return null;
                
                return (
                  <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      {hasPlayoffs && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, bgcolor: 'info.main', borderRadius: '50%' }} />
                          <Typography variant="caption" color="text.secondary">
                            Playoff spot
                          </Typography>
                        </Box>
                      )}
                      {playerAssignments.length > 0 && (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          flexWrap: 'wrap',
                          gap: { xs: 0.5, sm: 2 }
                        }}>
                          {playerAssignments.map((pa: any, index: number) => {
                            const playerTeam = divisionStandings.find(t => 
                              t.teamName.toLowerCase() === pa.teamName?.toLowerCase()
                            );
                            const playerName = pa.player?.nickname || pa.player?.name || 'Player';
                            
                            return playerTeam ? (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ 
                                  width: 12, 
                                  height: 12, 
                                  bgcolor: 'success.main', 
                                  borderRadius: '50%' 
                                }} />
                                <Typography variant="caption" color="text.secondary">
                                  {playerName}
                                </Typography>
                              </Box>
                            ) : null;
                          })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })()}
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                  Points per game
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {getPPGData(divisionStandings).map((team, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'flex-start' }}>
                        <Typography variant="caption" sx={{ fontSize: 10, flex: 1, pr: 1, lineHeight: 1.2, maxHeight: '2.4em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {team.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                          {team.ppg.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        position: 'relative', 
                        height: 16, 
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        {/* Background zones */}
                        <Box sx={{ 
                          position: 'absolute', 
                          left: 0, 
                          width: '50%', 
                          height: '100%', 
                          bgcolor: 'error.50' 
                        }} />
                        <Box sx={{ 
                          position: 'absolute', 
                          left: '50%', 
                          width: '25%', 
                          height: '100%', 
                          bgcolor: 'warning.50' 
                        }} />
                        <Box sx={{ 
                          position: 'absolute', 
                          left: '75%', 
                          width: '25%', 
                          height: '100%', 
                          bgcolor: 'success.50' 
                        }} />
                        {/* PPG bar */}
                        <Box sx={{ 
                          position: 'absolute',
                          left: 0,
                          width: `${team.percentage}%`,
                          height: '100%',
                          bgcolor: team.isPlayoff ? 'success.main' : 'primary.main',
                          transition: 'width 0.3s ease'
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Points per game progression - {divisionName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Each line shows a team's average points earned per match as the season progresses. Click any line to highlight it.
              </Typography>
              <ResponsiveContainer width="100%" height={window.innerWidth >= 600 ? 600 : 400}>
                <LineChart 
                  data={getRollingPPGData(divisionStandings)} 
                  margin={{ left: window.innerWidth >= 600 ? 20 : -30, right: 30, top: 15, bottom: 15 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="match" 
                    tick={{ fontSize: 11 }} 
                    label={{ value: 'Match Number', position: 'insideBottom', offset: -5, fontSize: 12 }} 
                  />
                  <YAxis 
                    domain={[0, 3]} 
                    tick={{ fontSize: 11 }} 
                    label={window.innerWidth >= 600 ? { value: 'Points Per Game', angle: -90, position: 'insideLeft', offset: 10, fontSize: 12 } : undefined} 
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length || !selectedTeam) return null;
                      const teamData = payload[0]?.payload;
                      if (!teamData || teamData[selectedTeam] === undefined) return null;
                      return (
                        <Box sx={{ bgcolor: 'background.paper', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="caption" display="block">Match {teamData.match}</Typography>
                          <Typography variant="caption" display="block" sx={{ color: teamColors.get(selectedTeam), fontWeight: 'bold' }}>
                            {selectedTeam}: {teamData[selectedTeam].toFixed(2)}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                  {divisionStandings.map(team => {
                    const isSelected = selectedTeam === team.teamName;
                    const isOtherSelected = selectedTeam && selectedTeam !== team.teamName;
                    
                    return (
                      <React.Fragment key={team.teamName}>
                        <Line 
                          type="monotone" 
                          dataKey={team.teamName} 
                          stroke="transparent"
                          strokeWidth={20}
                          dot={false}
                          activeDot={false}
                          isAnimationActive={false}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedTeam(selectedTeam === team.teamName ? null : team.teamName)}
                        />
                        <Line 
                          type="monotone" 
                          dataKey={team.teamName} 
                          stroke={teamColors.get(team.teamName) || '#999'}
                          strokeWidth={isSelected ? 4 : 2}
                          strokeOpacity={isOtherSelected ? 0.2 : 1}
                          dot={false}
                          isAnimationActive={false}
                          activeDot={isSelected ? { r: 6 } : false}
                        />
                      </React.Fragment>
                    );
                  })}
                  {divisionStandings.map(team => {
                    const isOtherSelected = selectedTeam && selectedTeam !== team.teamName;
                    
                    return (
                      <Line 
                        key={`${team.teamName}-logo`}
                        type="monotone" 
                        dataKey={team.teamName} 
                        stroke="transparent"
                        strokeWidth={0}
                        dot={false}
                        isAnimationActive={false}
                        label={(props: any) => {
                          const { x, y, index, value } = props;
                          if (value === undefined || value === null) return <></>;
                          
                          const data = getRollingPPGData(divisionStandings);
                          const hasDataAfter = data.slice(index + 1).some(d => d[team.teamName] !== undefined && d[team.teamName] !== null);
                          if (hasDataAfter) return <></>;
                          
                          return (
                            <foreignObject x={x - 12} y={y - 12} width={24} height={24} opacity={isOtherSelected ? 0.2 : 1}>
                              <div style={{ width: 24, height: 24 }}>
                                <TeamLogo 
                                  teamId={team.teamId} 
                                  teamName={team.teamName} 
                                  size={24}
                                  logoPath={team.logoPath}
                                  logoUrl={team.logoUrl}
                                />
                              </div>
                            </foreignObject>
                          );
                        }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, justifyContent: 'center' }}>
                {divisionStandings.map(team => {
                  const isSelected = selectedTeam === team.teamName;
                  const isOtherSelected = selectedTeam && selectedTeam !== team.teamName;
                  return (
                    <Box 
                      key={team.teamName} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        cursor: 'pointer',
                        opacity: isOtherSelected ? 0.4 : 1,
                        fontWeight: isSelected ? 'bold' : 'normal',
                        '&:hover': { opacity: 1 }
                      }}
                      onClick={() => setSelectedTeam(selectedTeam === team.teamName ? null : team.teamName)}
                    >
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: teamColors.get(team.teamName) || '#999',
                        border: isSelected ? '2px solid black' : 'none'
                      }} />
                      <Typography variant="caption" sx={{ fontSize: 10 }}>{team.teamName}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </React.Fragment>
        );
      })}
      </ContentWrapper>
    </ErrorBoundary>
  );
}
